import React from 'react'

/* -------------------------------------------------------------------------
 * "YOU ARE ALREADY ON A PANEL" — the one signal an overlay seam owes anything
 * it renders inside its own floating surface.
 *
 * THE DEFECT THIS EXISTS FOR. `src/antd/Dropdown` paints a real panel:
 * background, border, radius, shadow. A call site that supplies its own body
 * through `dropdownRender` very often supplies a seam `<Menu>`, and
 * `src/antd/Menu`'s `surface` defaults ON for `mode="vertical"` — so the Menu
 * paints a SECOND panel inside the first. Measured on the Containers list's
 * row-action menu: a 144x170 `[data-slot="dropdown-menu-content"]` with a 1px
 * border, 4px radius and a shadow, containing a 134x160 `[data-slot="menu"]`
 * with a 1px border, 5.2px radius and a shadow of its own.
 *
 * antd had an answer to exactly this and the seam cannot use it: antd's
 * Dropdown wraps its overlay in an `OverrideProvider` that swaps the Menu's
 * prefixCls (`ant-dropdown-menu` instead of `ant-menu`) and suppresses the
 * inner panel. That context is `@internal` to the package. This is the seam's
 * own version of the same idea, in the seam's own vocabulary.
 *
 * WHY A CONTEXT AND NOT A PROP AT EACH CALL SITE. There are five
 * `dropdownRender`-plus-`Menu` sites and only one of them ever passed
 * `surface={false}`; the other four each shipped the double panel. A prop is a
 * thing every future call site has to remember, and the ones that forget look
 * fine in a test and wrong on screen. The panel is a property of WHERE the
 * menu is rendered, so the component that renders the panel is what should say
 * so.
 *
 * An explicit `surface` prop still wins — that is what makes this safe to add
 * under existing call sites.
 *
 * Leaf module on purpose (the boot-static rule in AGENTS.md): `src/antd/Menu`
 * reads this, and must not import the Radix dropdown to do it.
 * ------------------------------------------------------------------------- */

const OverlaySurfaceContext = React.createContext(false)

/**
 * Marks its subtree as already sitting on an overlay seam's own floating
 * panel. Rendered by the seam that paints the panel, never by a call site.
 */
export const OverlaySurfaceProvider = ({
  children,
}: {
  children: React.ReactNode
}) => (
  <OverlaySurfaceContext.Provider value>
    {children}
  </OverlaySurfaceContext.Provider>
)

/** True inside an overlay seam's floating panel. */
export const useInsideOverlaySurface = () =>
  React.useContext(OverlaySurfaceContext)
